import { Modal, Form, Input, Button, Select, Row, Col, TimePicker, Switch } from "antd";
import { useRef, useState, useEffect } from "react";
import Draggable from "react-draggable";
import { useSelector } from "react-redux";

import { customerLocationMasterFormItemLayout, inputLayout2 } from "../../../../constants/ApplicationConstants/FormConstants";
import CircularProgressBar from "../../../common/CircularProgressBar";
import Loading from "../../../common/Loading";
import FormInputRules from "../../../../util/FormInputRules";
import { General } from "../../../../util/General";
import { ModuleAndIdMapping, Modules } from "../../../../constants/DatabaseConstants/MasterConstant";
import useGetOneMasterList from "../../../../hooks/useGetOneMasterList";
import { createHandleEditKeyPress, createHandleKeyPress, createHandlePinCodeKeyPress } from "../../../../util/handleKeyPress";
import styles from "../../index.module.less";
import { useReactDraggable } from "../../../../hooks/useReactDraggable";

import type FixMeLater from "../../../../shared/types/fixMeLater.type";
import type { RootState } from "../../../../appRedux/store";
import useValue from "../../../../hooks/useValue";
import moment from "moment";

type CustomerLocationMasterProps = {
  saving: boolean;
  addRecord: boolean;
  handleAddRecord: (value: boolean) => void;
  rowData: FixMeLater;
  handleClearRowIdAndActionType: () => void;
  handleSubmit: (data: FixMeLater) => void;
};

const CustomerLocationMaster = (props: CustomerLocationMasterProps) => {
  /* CONSTANTS */
  const [form] = Form.useForm();
  const formRef = useRef<FixMeLater>(null);

  /* STATES */
  const [formFieldsLoadedToForm, setFormFieldsLoadedToForm] = useState(false);

  /* CUSTOM HOOKS */
  // const { loading: customerListLoading, masterList: customerMasterList } = useGetOneMasterList(ModuleAndIdMapping[Modules.CUSTOMER_MASTER]);
  const { loading: locationTypeListLoading, masterList: locationTypeMasterList } = useGetOneMasterList(ModuleAndIdMapping[Modules.LOCATION_TYPE_MASTER]);
  const { loading: parkingMapTypeListLoading, masterList: parkingMapTypeMasterList } = useGetOneMasterList(ModuleAndIdMapping[Modules.PARKING_MAP_TYPE_MASTER]);
  const { value: isAvailableBySearch, handleValue: handleAvailableBySearch } = useValue(props.rowData?.is_available_by_search ?? false);
  const { masterFilters } = useSelector(({ common }: RootState) => common);

  const { bounds, onStart } = useReactDraggable();

  /* HELPER FUNCTIONS */
  const disabledTime = () => {
    const fromTime = form.getFieldValue("from_operating_time");
    if (!fromTime) return {};

    const fromHour = moment(fromTime, "HH:mm").hour();
    const fromMinute = moment(fromTime, "HH:mm").minute();

    return {
      disabledHours: () => Array.from({ length: fromHour + 1 }, (_, i) => i),
      disabledMinutes: (selectedHour: number) => (selectedHour === fromHour ? Array.from({ length: fromMinute + 1 }, (_, i) => i) : [])
    };
  };

  /* HANDLERS */

  /* loading states from multiple hooks added as expression */
  const isOnAddLoading = !(props.addRecord && !locationTypeListLoading && !parkingMapTypeListLoading);
  const isOnEditLoading = !(!props.addRecord! && !locationTypeListLoading && !parkingMapTypeListLoading);

  const handleSubmit = (data: any) => {
    const formattedData = {
      ...data,
      from_operating_time: data?.from_operating_time?.format("HH:mm") ?? null,
      to_operating_time: data?.to_operating_time?.format("HH:mm") ?? null,
      is_available_by_search: data?.is_available_by_search ?? false
    };

    if (props.addRecord) {
      props.handleSubmit({ ...formattedData, customer_id: masterFilters?.customerId });
    } else {
      props.handleSubmit({ ...formattedData, customer_id: props.rowData?.customer_id });
    }
  };

  /* EFFECTS */
  useEffect(() => {
    if (!props.addRecord && !isOnEditLoading && !formFieldsLoadedToForm) {
      const fromOperatingTime = props?.rowData?.from_operating_time ? moment(props?.rowData?.from_operating_time, "HH:mm:ss") : moment().startOf("day");
      const toOperatingTime = props?.rowData?.to_operating_time ? moment(props?.rowData?.to_operating_time, "HH:mm:ss") : moment().startOf("day");
      const formattedData = { ...props.rowData, from_operating_time: fromOperatingTime, to_operating_time: toOperatingTime };

      form.setFieldsValue(formattedData);
      setFormFieldsLoadedToForm(true);
    }
    // eslint-disable-next-line
  }, [isOnEditLoading]);

  return (
    <Modal
      afterClose={() => form.resetFields()}
      destroyOnClose={true}
      maskClosable={false}
      centered
      title={<div className="modal-container">{`${props.addRecord ? "Add" : "Edit"} Customer Location Master`}</div>}
      open={true}
      onCancel={props.addRecord ? props.handleAddRecord.bind(this, false) : props.handleClearRowIdAndActionType}
      width={"100%"}
      okButtonProps={{
        style: {
          display: "none"
        }
      }}
      cancelButtonProps={{ style: { display: "none" } }}
      modalRender={(modal) => (
        <Draggable bounds={bounds} handle=".ant-modal-header" onStart={(event, uiData) => onStart(event as any, uiData)}>
          {modal}
        </Draggable>
      )}
    >
      {((props.addRecord && isOnAddLoading) || (!props.addRecord && !formFieldsLoadedToForm)) && (
        <div className={`${styles["textCenter"]}`}>
          <Loading />
        </div>
      )}

      {((props.addRecord && !isOnAddLoading) || (!props.addRecord && formFieldsLoadedToForm)) && (
        <Form
          ref={formRef}
          onValuesChange={(changedValues) => {
            const fieldName = Object.keys(changedValues)[0];
            form.validateFields([fieldName]);
          }}
          className={`${styles["formDiv"]} ${styles["formDivExtended"]}`}
          {...customerLocationMasterFormItemLayout}
          form={form}
          validateTrigger="onBlur"
          colon={false}
          name="register"
          onFinish={handleSubmit}
        >
          <Row gutter={24}>
            <Col span={8} xs={24} sm={24} md={8} lg={8}>
              <Form.Item {...inputLayout2} name="location_type_id" label="Location Type" rules={[FormInputRules.requiredFieldSelectRule("location type name")]}>
                <Select className={`${styles["from-Item-selectDiv"]}`} showSearch allowClear listHeight={200} getPopupContainer={(trigger) => trigger.parentElement} filterOption={General.searchFilterOnSelect} placeholder="Select location type">
                  {locationTypeMasterList?.map((item) => (
                    <Select.Option key={item.id} value={item.id}>
                      {item.location_type_name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item {...inputLayout2} name="parking_map_type_id" label="Parking Map Type" rules={[FormInputRules.requiredFieldSelectRule("operation name")]}>
                <Select className={`${styles["from-Item-selectDiv"]}`} showSearch allowClear dropdownStyle={{ maxHeight: "200px", overflowY: "auto" }} getPopupContainer={(trigger) => trigger.parentElement} filterOption={General.searchFilterOnSelect} placeholder="Select parking map type">
                  {parkingMapTypeMasterList?.map((item) => (
                    <Select.Option key={item.id} value={item.id}>
                      {item.parking_map_type_name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item {...inputLayout2} name="location_name" label="Location Name" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("location name"), FormInputRules.ValidateFirstAndLastName()]}>
                <Input name="location_name" />
              </Form.Item>

              <Row gutter={24} justify="start" align="middle">
                <Col span={12}>
                  <Form.Item {...inputLayout2} name="device_id" label="Device Id" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("Device Id"), FormInputRules.allowOnlyAlphanumericAndHyphensRule()]}>
                    <Input name="device_id" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item {...inputLayout2} name="project_id" label="Project Id" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("Project Id"), FormInputRules.allowOnlyAlphanumericAndHyphensRule()]}>
                    <Input name="project_id" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item {...inputLayout2} name="contact_person" label="Contact Person" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("contact person"), FormInputRules.ValidateFirstAndLastName()]}>
                <Input name="contact_person" />
              </Form.Item>

              {props.addRecord ? (
                <Form.Item {...inputLayout2} name="mobile_number" label="Contact Number" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("contact number"), FormInputRules.ContactNumberValidation2Rule()]}>
                  <Input prefix="+91" name="mobile_number" onKeyPress={createHandleKeyPress()} />
                </Form.Item>
              ) : (
                <Form.Item {...inputLayout2} name="mobile_number" label="Contact Number" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("contact number"), FormInputRules.ContactNumberValidationRule()]}>
                  <Input name="mobile_number" onKeyPress={createHandleEditKeyPress()} />
                </Form.Item>
              )}

              <Form.Item {...inputLayout2} name="email_address" label="Email Address" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.EmailValidationRule()]}>
                <Input name="email_address" />
              </Form.Item>
            </Col>

            <Col span={8} xs={24} sm={24} md={8} lg={8}>
              <Form.Item {...inputLayout2} name="location_address" label="Location Address" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("location address")]}>
                <Input name="location_address" />
              </Form.Item>

              <Form.Item {...inputLayout2} name="city" label="City" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("city"), FormInputRules.allowOnlyCharactersRule()]}>
                <Input name="city" />
              </Form.Item>

              <Form.Item {...inputLayout2} name="state" label="State" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("state"), FormInputRules.allowOnlyCharactersRule()]}>
                <Input name="state" />
              </Form.Item>

              <Form.Item {...inputLayout2} name="country" label="Country" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("country"), FormInputRules.allowOnlyCharactersRule()]}>
                <Input name="country" />
              </Form.Item>

              <Form.Item {...inputLayout2} name="pin_code" label="Pin Code" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("pin code"), FormInputRules.PinCodeValidationRule("Pin code")]}>
                <Input name="pin_code" onKeyPress={createHandlePinCodeKeyPress()} />
              </Form.Item>

              <Form.Item {...inputLayout2} name="location_lat" label="Latitude" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.LatitudeLongitudeRule()]}>
                <Input name="location_lat" />
              </Form.Item>

              <Form.Item {...inputLayout2} name="location_long" label="Longitude" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.LatitudeLongitudeRule()]}>
                <Input name="location_long" />
              </Form.Item>
            </Col>

            <Col span={8} xs={24} sm={24} md={8} lg={8}>
              <Form.Item {...inputLayout2} name="is_available_by_search" valuePropName="checked" label="Is Available by Search?">
                <Switch checked={isAvailableBySearch} onChange={handleAvailableBySearch} />
              </Form.Item>

              {/* 
                  form item Operating Hours is added for label purpose, adding custom styles for a row will make the code more complex. Operating hours Form.Item is only for
                  labelling purpose.
              */}
              <Form.Item {...inputLayout2} style={{ marginBottom: 0 }} label="Operating Hours">
                <Row gutter={50} justify="center" align="middle">
                  <Col span={11}>
                    <Form.Item name="from_operating_time" rules={isAvailableBySearch ? [FormInputRules.requiredFieldInputRule("from time")] : []}>
                      <TimePicker format="HH:mm" name="from_operating_time" placeholder="From" disabled={!isAvailableBySearch} />
                    </Form.Item>
                  </Col>
                  <Col span={11}>
                    <Form.Item name="to_operating_time" rules={isAvailableBySearch ? [FormInputRules.requiredFieldInputRule("to time")] : []}>
                      <TimePicker format="HH:mm" name="to_operating_time" placeholder="To" disabled={!isAvailableBySearch} disabledTime={disabledTime} />
                    </Form.Item>
                  </Col>
                </Row>
              </Form.Item>

              <Form.Item {...inputLayout2} name="valet_parking_amount" label="Valet Parking Amount" rules={isAvailableBySearch ? [FormInputRules.requiredFieldInputRule("valet parking amount")] : []}>
                <Input name="valet_parking_amount" type="number" min={0} disabled={!isAvailableBySearch} />
              </Form.Item>

              <Form.Item {...inputLayout2} name="penalty_amount" label="Penalty Amount" rules={isAvailableBySearch ? [FormInputRules.requiredFieldInputRule("penalty amount")] : []}>
                <Input name="penalty_amount" type="number" min={0} disabled={!isAvailableBySearch} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item className={`${styles["form-itemFloatR20"]}`}>
            <Button htmlType="submit" className={`gx-mb-0 button-gradiant ${styles["button-m01"]}`}>
              {props.saving ? <CircularProgressBar /> : props.addRecord ? "Submit" : "Update"}
            </Button>
          </Form.Item>

          <Form.Item className={`${styles["form-itemFloatR20"]}`}>
            <Button disabled={props.saving} className={`gx-mb-0 button-gradiant ${styles["button-m01"]}`} onClick={props.addRecord ? props.handleAddRecord.bind(this, false) : props.handleClearRowIdAndActionType}>
              Close
            </Button>
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

export default CustomerLocationMaster;
